(function () {
  const productSwipers = new WeakMap();

  function copyText(value) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(value);
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
      return Promise.resolve();
    } finally {
      document.body.removeChild(textarea);
    }
  }

  async function addVariantToCart(variantId, button) {
    if (!variantId) return;

    button.disabled = true;

    try {
      if (window.PBCart && typeof window.PBCart.addLine === 'function') {
        await window.PBCart.addLine(variantId, 1);
        return;
      }

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
        }),
      });

      if (!response.ok) {
        throw new Error('Add to cart failed');
      }

      document.dispatchEvent(new CustomEvent('pb:cart:updated'));
    } catch (error) {
      console.error('[WONDR SEO Blog] Add to cart failed', error);
    } finally {
      button.disabled = false;
    }
  }

  function destroyProductSwipers(root) {
    const containers = root.querySelectorAll('[data-wondr-seo-blog-products]');

    for (const container of containers) {
      const instance = productSwipers.get(container);

      if (instance) {
        instance.destroy(true, true);
        productSwipers.delete(container);
      }
    }
  }

  function initProductSwipers(root) {
    if (typeof Swiper === 'undefined') return;

    const containers = root.querySelectorAll('[data-wondr-seo-blog-products]');

    for (const container of containers) {
      const slides = container.querySelectorAll('.swiper-slide');

      if (slides.length <= 1) continue;

      const paginationEl = container.closest('.wondr-seo-blog__product-carousel')?.querySelector(
        '.wondr-seo-blog__product-pagination'
      );
      const prevEl = container
        .closest('.wondr-seo-blog__product-carousel')
        ?.querySelector('.wondr-seo-blog__product-arrow--prev');
      const nextEl = container
        .closest('.wondr-seo-blog__product-carousel')
        ?.querySelector('.wondr-seo-blog__product-arrow--next');
      const instance = new Swiper(container, {
        slidesPerView: 1,
        spaceBetween: 20,
        autoHeight: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        pagination: paginationEl
          ? {
              el: paginationEl,
              clickable: true,
            }
          : false,
        navigation:
          prevEl && nextEl
            ? {
                prevEl,
                nextEl,
              }
            : false,
      });

      productSwipers.set(container, instance);
    }
  }

  function normalizeShareUrl(url) {
    if (!url) return url;

    if (url.startsWith('http://')) {
      return `https://${url.slice('http://'.length)}`;
    }

    return url;
  }

  function buildLinkedInShareUrl(shareUrl) {
    const normalizedUrl = normalizeShareUrl(shareUrl);
    const encodedUrl = encodeURIComponent(normalizedUrl);

    return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
  }

  function getShareUrl(shareRoot) {
    const datasetUrl = shareRoot.dataset.shareUrl?.trim();
    if (datasetUrl) return normalizeShareUrl(datasetUrl);

    return normalizeShareUrl(
      `${window.location.origin}${window.location.pathname}${window.location.search}`
    );
  }

  function updateShareLinks(root) {
    const titleEl = root.querySelector('.wondr-seo-blog__title');
    const shareRoots = root.querySelectorAll('[data-wondr-seo-blog-share]');

    for (const shareRoot of shareRoots) {
      const shareUrl = getShareUrl(shareRoot);
      const shareTitle = shareRoot.dataset.shareTitle?.trim() || titleEl?.textContent?.trim() || '';
      const encodedUrl = encodeURIComponent(shareUrl);
      const encodedTitle = encodeURIComponent(shareTitle);

      const facebook = shareRoot.querySelector('.wondr-seo-blog__share-link--facebook');
      if (facebook) {
        facebook.href = `https://www.facebook.com/sharer.php?u=${encodedUrl}`;
      }

      const linkedin = shareRoot.querySelector('.wondr-seo-blog__share-link--linkedin');
      if (linkedin) {
        linkedin.href = buildLinkedInShareUrl(shareUrl);
      }

      const twitter = shareRoot.querySelector('.wondr-seo-blog__share-link--x');
      if (twitter) {
        twitter.href = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
      }
    }
  }

  function initShareControls(root) {
    updateShareLinks(root);

    if (root.dataset.shareBound === 'true') return;
    root.dataset.shareBound = 'true';

    root.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-wondr-seo-blog-copy-link]');
      if (!button || !root.contains(button)) return;

      event.preventDefault();

      try {
        await copyText(window.location.href);
        button.classList.add('is-copied');

        const label = button.querySelector('.wondr-seo-blog__share-copy-label');
        const previousLabel = label?.textContent;

        if (label) {
          label.textContent = 'COPIED';
        }

        window.setTimeout(() => {
          button.classList.remove('is-copied');
          if (label && previousLabel) {
            label.textContent = previousLabel;
          }
        }, 2000);
      } catch (error) {
        console.error('[WONDR SEO Blog] Copy link failed', error);
      }
    });
  }

  function initProductButtons(root) {
    const buttons = root.querySelectorAll('[data-wondr-seo-blog-atc]');

    for (const button of buttons) {
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        await addVariantToCart(button.dataset.variantId, button);
      });
    }
  }

  function initFaqControls(root) {
    if (root.dataset.faqBound === 'true') return;
    root.dataset.faqBound = 'true';

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-wondr-seo-blog-faq-trigger]');
      if (!trigger || !root.contains(trigger)) return;

      const item = trigger.closest('.wondr-seo-blog__faq-item');
      if (!item) return;

      const isOpen = item.classList.contains('is-open');
      item.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  }

  function initSection(section) {
    destroyProductSwipers(section);
    initShareControls(section);
    initFaqControls(section);
    initProductButtons(section);
    initProductSwipers(section);
  }

  function initAll() {
    const sections = document.querySelectorAll('[data-wondr-seo-blog-article]');

    for (const section of sections) {
      initSection(section);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector?.('[data-wondr-seo-blog-article]');

    if (section) {
      initSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector?.('[data-wondr-seo-blog-article]');

    if (section) {
      destroyProductSwipers(section);
    }
  });
})();
